import { useEffect, useState, useRef, useCallback } from "react";
import api from "./api";
import { brandName } from "./brand";
import { toast } from "../components/ui/sonner";

// Iter 364 (2026-02-27) — Dirk: "We should get notifications as
// reliably as WhatsApp." Bumped from 45s → 8s so a photo uploaded on
// one device shows up on another within seconds. The endpoint is a
// cheap indexed query (see routes/notifications.py) — the extra load
// is negligible next to the UX win.
const POLL_MS = 8_000;

/**
 * Polls /api/notifications for the current user and exposes:
 *   { items, unread, markRead, markAllRead, requestPermission, permission }
 *
 * Side-effect: when new unread notifications arrive across polls, fires a
 * desktop notification (if the user granted permission via requestPermission).
 */
export function useUserNotifications({ enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [procurementOverdue, setProcurementOverdue] = useState({ count: 0, worst_days: 0 });
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  const seenIdsRef = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const list = data?.items || [];
      // Detect brand-new notifications since last poll
      const fresh = list.filter(
        (n) => !seenIdsRef.current.has(n.id) && !n.is_read,
      );
      // Only fire desktop notif from the SECOND poll onwards (avoid blast on login)
      if (seenIdsRef.current.size > 0 && fresh.length > 0) {
        // Iter 364 (2026-02-27) — Dirk: "notifications are one
        // hundred percent crucial." Fire BOTH the desktop
        // notification (works when the tab is backgrounded and the
        // user granted permission) AND an in-app toast so the user
        // never misses an update even if permission was denied or the
        // tab is focused. WhatsApp-style: chime + banner in-app,
        // native alert when in the background.
        fresh.slice(0, 3).forEach((n) => {
          // In-app toast (always fires — bright banner, click to nav).
          try {
            toast(`${n.title}`, {
              description: n.body || "",
              action: n.link ? {
                label: "Open",
                onClick: () => { window.location.href = n.link; },
              } : undefined,
              duration: 6000,
            });
          } catch (_) { /* toast lib not mounted */ }
          if (permission === "granted") {
            try {
              const note = new Notification(`${brandName()} · ${n.title}`, {
                body: n.body || "",
                icon: "/icons/icon-192.png",
                badge: "/icons/icon-192.png",
                tag: n.id,
              });
              note.onclick = () => {
                window.focus();
                if (n.link) window.location.href = n.link;
              };
            } catch (_) {
              /* ignore */
            }
          }
        });
      }
      seenIdsRef.current = new Set(list.map((n) => n.id));
      setItems(list);
      setUnread(data?.unread || 0);
    } catch (_) {
      /* keep polling */
    }
    // Iter 408.1 — piggy-back the procurement overdue-alerts feed on
    // the same poll cadence so the bell tile stays fresh. Best-effort;
    // failures are silent and reset the badge to zero.
    try {
      const { data: ov } = await api.get("/procurement/alerts/overdue");
      const overdueItems = ov?.items || [];
      const worst = overdueItems.length
        ? Math.max(...overdueItems.map((a) => Number(a.days_overdue || 0)))
        : 0;
      setProcurementOverdue({ count: overdueItems.length, worst_days: worst });
    } catch (_) {
      setProcurementOverdue({ count: 0, worst_days: 0 });
    }
  }, [permission]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    // Refresh when window regains focus (cheap freshness)
    const onFocus = () => refresh();
    // 2026-02-20 (iter 172, Dirk) — listen for the cross-component
    // "notifications mutated elsewhere" event so the bell and the
    // AlertsPage stay in lock-step. Before this, AlertsPage's
    // mark-all-read left the bell counter stale for up to 45s
    // (next scheduled poll) — Dirk saw bell=16 + AlertsPage=0.
    const onMutated = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("hv:notifications-mutated", onMutated);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hv:notifications-mutated", onMutated);
    };
  }, [enabled, refresh]);

  const _broadcast = () => {
    try {
      window.dispatchEvent(new CustomEvent("hv:notifications-mutated"));
    } catch { /* old browsers */ }
  };

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (_) {
      /* will reconcile next poll */
    }
    _broadcast();
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
    try {
      await api.post("/notifications/read-all");
    } catch (_) {
      /* reconcile */
    }
    _broadcast();
  };

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return "denied";
    const p = await Notification.requestPermission();
    setPermission(p);
    return p;
  };

  return { items, unread, markRead, markAllRead, refresh, permission, requestPermission, procurementOverdue };
}
